import './WorkbenchPages.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import http from '../../../api/http';
import PageHero from '../../../components/page/PageHero';
import StatusBadge from '../../../components/ui/StatusBadge';

const WRITE_ACTIONS = ['set_field', 'create_child', 'transition', 'link'];
const OUTCOMES = ['success', 'failed', 'skipped_loop'];

const messageOf = (error, fallback) => error?.response?.data?.message || error?.message || fallback;
const when = (value) => (value ? new Date(value).toLocaleString() : '—');
const sectionOf = (pattern) => {
  const match = String(pattern || '').match(/^([A-Za-z][A-Za-z0-9_]*)\./);
  return match ? match[1] : null;
};

const triggerText = (rule) => {
  const condition = rule.conditionField
    ? ` when ${rule.conditionField} ${String(rule.conditionOperator || '').replace(/_/g, ' ')}${rule.conditionOperator === 'exists' ? '' : ` "${rule.conditionValue}"`}`
    : '';
  return `${rule.eventPattern || '—'}${condition}`;
};

const WriteSummary = ({ write }) => {
  const target = write.kind === 'link'
    ? `${write.type}: ${write.from?.section} ${write.from?.id} → ${write.to?.section} ${write.to?.id}`
    : `${write.action} ${write.section}${write.id ? ` ${write.id}` : ''}`;
  return (
    <li>
      <strong>{target}</strong>
      {write.body ? <span className="small-muted"> {JSON.stringify(write.body)}</span> : null}
      {write.outcome ? <span> — {write.outcome.replace(/_/g, ' ')}</span> : null}
      {write.approvalId ? <span className="small-muted"> (approval {write.approvalId})</span> : null}
      {write.before ? <span className="small-muted"> · before {JSON.stringify(write.before)}</span> : null}
      {write.wouldChange === false ? <span className="small-muted"> · no change</span> : null}
      {write.transition?.mode === 'approval' ? (
        <span className="workbench-warning"> · needs approval by {write.transition.requiresApproval.count} {write.transition.requiresApproval.role}(s)</span>
      ) : null}
      {write.transition?.mode === 'blocked' ? <span className="workbench-warning"> · {write.transition.reason}</span> : null}
    </li>
  );
};

const ExecutionTable = ({ items, rulesById }) => (
  <div className="table-scroll">
    <table className="data-table">
      <thead>
        <tr>
          <th>When</th>
          {rulesById ? <th>Rule</th> : null}
          <th>Event</th>
          <th>Outcome</th>
          <th>Depth</th>
          <th>Writes / error</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id}>
            <td>{when(item.createdAt)}</td>
            {rulesById ? <td>{rulesById.get(String(item.ruleId))?.name || item.ruleId}</td> : null}
            <td>{item.eventSection ? `${item.eventSection} ${item.eventRecordId || ''}` : item.eventKey}</td>
            <td><StatusBadge value={item.outcome} /></td>
            <td>{item.depth}</td>
            <td>
              {item.error ? <span className="workbench-warning">{item.error}</span> : null}
              {(item.writes || []).length ? (
                <ul className="workbench-list">{item.writes.map((write, index) => <WriteSummary key={index} write={write} />)}</ul>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const RuleDetail = ({ rule }) => {
  const [executions, setExecutions] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [simulation, setSimulation] = useState(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const section = sectionOf(rule.eventPattern);

  useEffect(() => {
    let active = true;
    setSimulation(null);
    setEventId('');
    setError('');
    Promise.all([
      http.get(`/rules/${encodeURIComponent(rule.id)}/executions`, { params: { limit: 50 } }),
      section ? http.get('/foundations/events', { params: { section, limit: 25 } }).catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
    ])
      .then(([executionsResponse, eventsResponse]) => {
        if (!active) return;
        setExecutions(Array.isArray(executionsResponse.data?.items) ? executionsResponse.data.items : []);
        setEvents(Array.isArray(eventsResponse.data?.data) ? eventsResponse.data.data : []);
      })
      .catch((requestError) => { if (active) setError(messageOf(requestError, 'Unable to load rule activity.')); });
    return () => { active = false; };
  }, [rule.id, section]);

  async function simulate(event) {
    event.preventDefault();
    if (!eventId) return;
    setRunning(true);
    setError('');
    try {
      const { data } = await http.post(`/rules/${encodeURIComponent(rule.id)}/simulate`, { eventId });
      setSimulation(data);
    } catch (requestError) {
      setError(messageOf(requestError, 'Unable to test-run the rule.'));
    } finally {
      setRunning(false);
    }
  }

  const isWrite = WRITE_ACTIONS.includes(String(rule.actionType || '').toLowerCase());

  return (
    <section className="card stack">
      <div className="row-between">
        <div>
          <h2 className="section-title">{rule.name}</h2>
          <p className="small-muted">{triggerText(rule)} → {rule.actionType}</p>
        </div>
        {rule.slug ? <Link className="button-secondary" to={`/rules/${encodeURIComponent(rule.slug)}/edit`}>Edit rule</Link> : null}
      </div>
      {isWrite ? (
        <p className={rule.runAsRole ? 'small-muted' : 'workbench-warning'}>
          {rule.runAsRole
            ? `Writes run as role "${rule.runAsRole}". Approval-gated transitions open an approval request.`
            : 'Writes run with no role: role-restricted transitions will fail. Set "Run Write Actions As Role" to delegate a role.'}
        </p>
      ) : null}
      {rule.actionConfig ? <pre className="workbench-pre">{rule.actionConfig}</pre> : null}
      {error ? <p className="error-text" role="alert">{error}</p> : null}

      <h3 className="section-title">Test-run against a past event</h3>
      {events.length ? (
        <form className="workbench-form" onSubmit={simulate}>
          <select aria-label="Event" value={eventId} onChange={(event) => setEventId(event.target.value)}>
            <option value="">Choose a recent {section} event…</option>
            {events.map((item) => (
              <option key={item.id} value={item.id}>
                #{item.id} {item.action} {item.section} {item.recordId} · {when(item.occurredAt)}
              </option>
            ))}
          </select>
          <button className="button-primary" type="submit" disabled={!eventId || running}>{running ? 'Running…' : 'Test-run'}</button>
        </form>
      ) : <p className="small-muted">No recorded events for {section || 'this pattern'} yet.</p>}
      {simulation ? (
        <div className="stack">
          <p className={simulation.wouldFire ? 'workbench-ok' : 'small-muted'}>
            {simulation.wouldFire
              ? 'Would fire.'
              : `Would not fire: ${simulation.patternMatched ? 'condition not met' : 'event does not match the pattern'}.`}
            {' '}Nothing was changed.
          </p>
          {simulation.runAs && !simulation.runAs.valid ? <p className="workbench-warning">{simulation.runAs.error}</p> : null}
          {simulation.writes ? (
            simulation.writes.length
              ? <ul className="workbench-list">{simulation.writes.map((write, index) => <WriteSummary key={index} write={write} />)}</ul>
              : <p className="small-muted">No targets resolved for this event.</p>
          ) : null}
          {simulation.notification ? (
            <p>Notify {simulation.notification.recipients} user(s): <strong>{simulation.notification.title}</strong> — {simulation.notification.body}</p>
          ) : null}
          {simulation.webhook ? <pre className="workbench-pre">{JSON.stringify(simulation.webhook, null, 2)}</pre> : null}
        </div>
      ) : null}

      <h3 className="section-title">Run history</h3>
      {executions.length ? <ExecutionTable items={executions} /> : <p className="small-muted">This rule has not run yet.</p>}
    </section>
  );
};

const AutomationPage = () => {
  const [rules, setRules] = useState([]);
  const [feed, setFeed] = useState([]);
  const [outcome, setOutcome] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    return Promise.all([
      http.get('/automation/rules'),
      http.get('/automation/executions', { params: { limit: 50, ...(outcome ? { outcome } : {}) } }),
    ])
      .then(([rulesResponse, feedResponse]) => {
        setRules(Array.isArray(rulesResponse.data?.items) ? rulesResponse.data.items : []);
        setFeed(Array.isArray(feedResponse.data?.items) ? feedResponse.data.items : []);
      })
      .catch((requestError) => setError(messageOf(requestError, 'Unable to load automation.')))
      .finally(() => setLoading(false));
  }, [outcome]);

  useEffect(() => { load(); }, [load]);

  const rulesById = useMemo(() => new Map(rules.map((rule) => [String(rule.id), rule])), [rules]);
  const selected = rulesById.get(String(selectedId)) || null;

  return (
    <div className="stack">
      <PageHero title="Automation" subtitle="What your rules did, what they would do, and which role they act with." />
      {loading ? <p>Loading automation…</p> : null}
      {error ? <p className="error-text" role="alert">{error}</p> : null}

      <section className="card stack">
        <div className="row-between">
          <h2 className="section-title">Rules</h2>
          <button className="button-secondary" type="button" onClick={load}>Refresh</button>
        </div>
        {!loading && !rules.length ? <p className="small-muted">No rules yet. Create one under Rules.</p> : null}
        {rules.length ? (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Status</th>
                  <th>Trigger</th>
                  <th>Action</th>
                  <th>Runs as</th>
                  <th>Last 7 days</th>
                  <th>Last run</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => {
                  const counts = rule.lastWeek?.counts || {};
                  const isWrite = WRITE_ACTIONS.includes(String(rule.actionType || '').toLowerCase());
                  return (
                    <tr key={rule.id}>
                      <td>
                        <button
                          className={`button-secondary${String(selectedId) === String(rule.id) ? ' workbench-selected' : ''}`}
                          type="button"
                          onClick={() => setSelectedId(rule.id)}
                        >
                          {rule.name}
                        </button>
                      </td>
                      <td><StatusBadge value={rule.status} /></td>
                      <td>{triggerText(rule)}</td>
                      <td>{rule.actionType}</td>
                      <td>{isWrite ? (rule.runAsRole || <span className="small-muted">no role</span>) : <span className="small-muted">n/a</span>}</td>
                      <td>
                        {Object.keys(counts).length
                          ? Object.entries(counts).map(([key, value]) => `${value} ${key.replace(/_/g, ' ')}`).join(', ')
                          : <span className="small-muted">no runs</span>}
                      </td>
                      <td>{when(rule.lastWeek?.lastRunAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {selected ? <RuleDetail key={selected.id} rule={selected} /> : null}

      <section className="card stack">
        <div className="row-between">
          <h2 className="section-title">Latest runs across all rules</h2>
          <select aria-label="Outcome filter" value={outcome} onChange={(event) => setOutcome(event.target.value)}>
            <option value="">All outcomes</option>
            {OUTCOMES.map((value) => <option key={value} value={value}>{value.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        {feed.length ? <ExecutionTable items={feed} rulesById={rulesById} /> : <p className="small-muted">No rule runs recorded.</p>}
      </section>
    </div>
  );
};

export default AutomationPage;
